import { formatMoney } from '@aeris/money';
import { formatDocumentNumber } from "./document-number.js";
import { customerFacts, infoBlocks, } from "./info-lines.js";
import { documentTemplate, footerBlocks, headerBlocks, itemLocation, itemName, styleOf, templateOr, } from "./receipt-template.js";
/** `05/08/2026 14:32`, without `Intl` and without a timezone surprise. */
const stamp = (at) => {
    const pad = (value) => String(value).padStart(2, '0');
    return (`${pad(at.getDate())}/${pad(at.getMonth() + 1)}/${String(at.getFullYear())}` +
        ` ${pad(at.getHours())}:${pad(at.getMinutes())}`);
};
export const receiptFor = (input) => {
    const model = templateOr(input.template);
    const doc = documentTemplate(input.template, 'receipt');
    const word = input.documentKind === 'order' ? 'Pedido' : 'Venda';
    const blocks = headerBlocks({
        company: input.company,
        branchName: input.branchName,
        template: input.template,
    });
    blocks.push({ kind: 'rule' });
    /*
     * Said plainly, at the top, before the figures.
     *
     * A customer who is owed a cupom fiscal must be able to tell in one glance
     * that this is not one, and a shop that prints this instead of an NFC-e must
     * not be able to claim it looked official. O texto é da loja (F149), mas o
     * contrato recusa deixá-lo vazio no cupom.
     */
    if (doc.notice !== null) {
        blocks.push({ kind: 'text', text: doc.notice, align: 'center', bold: true }, { kind: 'rule' });
    }
    /*
     * As linhas de dados, na ordem que a loja escolheu no editor (F160).
     *
     * "Pedido" quando é pedido: o recibo de um pedido reimpresso saía como
     * "Venda nº", e a loja passou a ter a Venda 16 e o Pedido 16 com o mesmo papel.
     */
    blocks.push(...infoBlocks(model, {
        number: {
            kind: 'pair',
            label: word,
            value: input.number === null
                ? 'sem número'
                : `nº ${formatDocumentNumber(input.number)}`,
        },
        date: { kind: 'pair', label: 'Data', value: stamp(input.issuedAt) },
        customer: { kind: 'pair', label: 'Cliente', value: input.customerName },
        ...customerFacts(model, input.customer, input.customerName),
        operator: model.receipt.showOperator && input.operatorName !== null
            ? { kind: 'pair', label: 'Operador', value: input.operatorName }
            : null,
        terminal: model.receipt.showTerminal && input.terminal !== null
            ? { kind: 'pair', label: 'Caixa', value: input.terminal }
            : null,
    }));
    blocks.push({ kind: 'rule' });
    /*
     * Two lines per item, always.
     *
     * The description gets a line of its own because a product name is longer
     * than any thermal paper, and the arithmetic gets the next one — so
     * "2 x 12,50" sits directly under what it multiplied, and a customer
     * checking the maths never has to guess which quantity belongs to which
     * product.
     */
    for (const line of input.lines) {
        blocks.push({ kind: 'text', text: itemName(model, line) });
        const location = itemLocation(model, line);
        if (location !== null)
            blocks.push({ kind: 'text', text: location });
        blocks.push({
            kind: 'pair',
            label: model.lines.showUnitMath
                ? `  ${line.quantity} ${line.unit} x ${formatMoney(line.unitPrice, { symbol: false })}`
                : `  ${line.quantity} ${line.unit}`,
            value: formatMoney(line.lineTotal, { symbol: false }),
        });
        if (line.priceOverridden === true && model.lines.showOverriddenMark) {
            // The customer is entitled to know a price was not the table's, and the
            // shop is entitled to see it on the copy it keeps. F026 records the
            // departure; this is where it becomes visible without a query.
            blocks.push({ kind: 'text', text: '  (preço alterado)' });
        }
        // O desconto do item, embaixo da conta dele (F161): o papel antigo tinha a
        // coluna "Des/Acr", e o cliente confere item por item.
        if (model.lines.showLineDiscount &&
            line.discount !== undefined &&
            line.discount.units !== 0n) {
            blocks.push({
                kind: 'pair',
                label: '  desconto',
                value: `-${formatMoney(line.discount, { symbol: false })}`,
            });
        }
    }
    blocks.push({ kind: 'rule' }, {
        kind: 'pair',
        label: 'Subtotal',
        value: formatMoney(input.subtotal, { symbol: false }),
    });
    // Only when there was one: a line reading "Desconto 0,00" invites the
    // question of what discount, on every receipt, forever.
    if (input.discount.units !== 0n) {
        blocks.push({
            kind: 'pair',
            label: 'Desconto',
            value: `-${formatMoney(input.discount, { symbol: false })}`,
        });
    }
    blocks.push({ kind: 'text', text: 'TOTAL', big: true, bold: true }, {
        kind: 'text',
        text: formatMoney(input.total),
        align: 'right',
        big: true,
        bold: true,
    }, { kind: 'rule' });
    for (const payment of input.payments) {
        blocks.push({
            kind: 'pair',
            label: payment.method,
            value: formatMoney(payment.amount, { symbol: false }),
        });
    }
    if (input.changeDue.units !== 0n) {
        blocks.push({
            kind: 'pair',
            label: 'Troco',
            value: formatMoney(input.changeDue, { symbol: false }),
            bold: true,
        });
    }
    const voucher = input.voucher ?? null;
    if (voucher !== null &&
        (voucher.balance.units !== 0n || voucher.lastRedeemed !== null)) {
        blocks.push({ kind: 'rule' }, {
            kind: 'pair',
            label: 'Saldo de vale',
            value: formatMoney(voucher.balance, { symbol: false }),
            bold: true,
        });
        if (voucher.lastRedeemed !== null) {
            blocks.push({
                kind: 'pair',
                label: `Último abatimento ${stamp(voucher.lastRedeemed.at).slice(0, 10)}`,
                value: `-${formatMoney(voucher.lastRedeemed.amount, { symbol: false })}`,
            });
        }
    }
    blocks.push({ kind: 'feed', lines: 1 });
    /*
     * Scanned to find the sale again, which is what an exchange or a complaint
     * starts with.
     *
     * The number, not the id: a UUID is 36 characters and CODE128 cannot print
     * that across 58mm paper at a width a scanner can read — it would come out
     * as bars nobody can use, taking the space of something they could. A draft
     * has no number and so gets no barcode; there is nothing to look up yet.
     */
    if (doc.showBarcode && input.number !== null) {
        blocks.push({
            kind: 'barcode',
            symbology: 'code128',
            /*
             * Seis dígitos, com zero à esquerda (27/09). "74" dava um símbolo de
             * 17 mm e quatro caracteres — curto para leitor configurado com
             * tamanho mínimo, e sem folga para a impressão. A busca de Vendas tira
             * os zeros, então `000074` abre a venda 74.
             */
            data: String(input.number).padStart(6, '0'),
        });
    }
    blocks.push({ kind: 'feed', lines: 1 });
    // Small, and last. Nobody reads it until somebody calls for help, and then it
    // is the only thing that identifies this sale unambiguously.
    if (doc.showDocumentId) {
        blocks.push({ kind: 'text', text: input.documentId, align: 'center' });
    }
    // "Obrigado pela preferência!" é o padrão, e a loja troca por outra coisa.
    blocks.push(...footerBlocks(input.template, 'receipt'), { kind: 'cut' });
    return {
        kind: 'receipt',
        title: input.number === null
            ? `Recibo de ${word.toLowerCase()}`
            : `Recibo ${input.documentKind === 'order' ? 'do pedido' : 'da venda'} nº ${String(input.number)}`,
        blocks,
        style: styleOf(input.template),
    };
};
//# sourceMappingURL=receipt.js.map